import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Search,
  Handshake,
  Users,
  Briefcase,
  Mail,
  Phone,
  MapPin,
  Star,
  CheckCircle,
  Clock,
} from "lucide-react";

// Mock data
const MOCK_CONSULTANTS = [
  {
    id: "1",
    name: "Dr. Sarah Finance",
    type: "external",
    specialty: "Financial Advisory",
    location: "Montreal, QC",
    rating: 4.8,
    dealsCompleted: 12,
    status: "active",
  },
  {
    id: "2",
    name: "Marc Legal",
    type: "internal",
    specialty: "Business Law",
    location: "Toronto, ON",
    rating: 4.9,
    dealsCompleted: 28,
    status: "active",
  },
  {
    id: "3",
    name: "Lisa Tax",
    type: "internal",
    specialty: "Tax Advisory",
    location: "Vancouver, BC",
    rating: 4.7,
    dealsCompleted: 15,
    status: "busy",
  },
];

const MOCK_ASSIGNMENTS = [
  {
    id: "1",
    dealName: "Tech Startup ABC",
    partnerName: "Marc Legal",
    taskType: "Contract Review",
    status: "in_progress",
    dueDate: new Date(Date.now() + 172800000),
  },
  {
    id: "2",
    dealName: "Restaurant Chain",
    partnerName: "Lisa Tax",
    taskType: "Tax Structuring",
    status: "pending",
    dueDate: new Date(Date.now() + 345600000),
  },
  {
    id: "3",
    dealName: "Manufacturing Co",
    partnerName: "Dr. Sarah Finance",
    taskType: "Valuation",
    status: "completed",
    dueDate: new Date(Date.now() - 86400000),
  },
];

export default function ConsultantsPartners() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("assignments");
  const [typeFilter, setTypeFilter] = useState("all");

  const filteredConsultants = MOCK_CONSULTANTS.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || c.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Consultants & Partners</h1>
          <p className="text-white/60 mt-1">
            Manage partner assignments and consultant directory
          </p>
        </div>
        <Button className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90">
          <Users className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Users className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Total Partners</p>
                <p className="text-2xl font-bold text-white">{MOCK_CONSULTANTS.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <Handshake className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Internal</p>
                <p className="text-2xl font-bold text-white">
                  {MOCK_CONSULTANTS.filter((c) => c.type === "internal").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Briefcase className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">External</p>
                <p className="text-2xl font-bold text-white">
                  {MOCK_CONSULTANTS.filter((c) => c.type === "external").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Open Tasks</p>
                <p className="text-2xl font-bold text-white">
                  {MOCK_ASSIGNMENTS.filter((a) => a.status !== "completed").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/10">
          <TabsTrigger value="assignments" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Partner Tasks
          </TabsTrigger>
          <TabsTrigger value="internal" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Internal Partners
          </TabsTrigger>
          <TabsTrigger value="external" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            External Consultants
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input
          placeholder="Search partners..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
        />
      </div>

      {/* Assignments View */}
      {activeTab === "assignments" && (
        <Card className="bg-white/10 border-white/20">
          <CardHeader>
            <CardTitle className="text-white">Active Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {MOCK_ASSIGNMENTS.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-2 rounded ${
                        assignment.status === "completed"
                          ? "bg-green-500/20"
                          : assignment.status === "in_progress"
                          ? "bg-blue-500/20"
                          : "bg-yellow-500/20"
                      }`}
                    >
                      {assignment.status === "completed" ? (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      ) : (
                        <Clock className="h-5 w-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white">{assignment.taskType}</p>
                      <p className="text-sm text-white/60">
                        {assignment.dealName} • {assignment.partnerName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge
                      variant="outline"
                      className={
                        assignment.status === "completed"
                          ? "text-green-400 border-green-400/50"
                          : assignment.status === "in_progress"
                          ? "text-blue-400 border-blue-400/50"
                          : "text-yellow-400 border-yellow-400/50"
                      }
                    >
                      {assignment.status.replace("_", " ")}
                    </Badge>
                    <span className="text-sm text-white/60">
                      Due: {assignment.dueDate.toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partners Grid */}
      {(activeTab === "internal" || activeTab === "external") && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConsultants
            .filter((c) => (activeTab === "internal" ? c.type === "internal" : c.type === "external"))
            .map((consultant) => (
              <Card key={consultant.id} className="bg-white/10 border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
                        <span className="text-lg font-semibold text-brand-gold">
                          {consultant.name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{consultant.name}</h3>
                        <p className="text-sm text-white/60">{consultant.specialty}</p>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        consultant.status === "active"
                          ? "bg-green-500/20 text-green-400 border-green-500/50"
                          : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                      }
                    >
                      {consultant.status}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <MapPin className="h-4 w-4" />
                      {consultant.location}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <Star className="h-4 w-4 text-yellow-400" />
                      {consultant.rating} • {consultant.dealsCompleted} deals
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" className="flex-1 border-white/20 text-white hover:bg-white/10">
                      <Mail className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                    <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}
